import type { ListEnvelope } from './types'

export const ALL_PAGES_PER_PAGE = 100
export const ALL_PAGES_MAX_PAGES = 100

export interface PageInfo {
  page: number
  lastPage: number
  total: number
}

export interface FetchAllPagesOptions<T> {
  /** Rows per request. v3 caps per_page at 400; 100 keeps each answer small. */
  perPage?: number
  /** Hard stop so a runaway filter cannot pull the whole table. */
  maxPages?: number
  /** Called once per page with that page's rows, so a caller can draw as it goes. */
  onPage?: (rows: T[], info: PageInfo) => void
}

export interface AllPages<T> {
  /** Every row fetched, in page order. */
  rows: T[]
  /** The server's count of matching rows. */
  total: number
  /** True when maxPages stopped the walk before the server's last page. */
  truncated: boolean
}

/**
 * Walk every page of a v3 list.
 *
 * The first page's meta decides how many more to fetch; there is no probe
 * request. Pages are fetched in order so progress is monotonic and the API is
 * never hit by a burst. A page shorter than perPage ends the walk early, since
 * the set shrank while paging and nothing follows it.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, perPage: number) => Promise<ListEnvelope<T>>,
  { perPage = ALL_PAGES_PER_PAGE, maxPages = ALL_PAGES_MAX_PAGES, onPage }: FetchAllPagesOptions<T> = {}
): Promise<AllPages<T>> {
  const first = await fetchPage(1, perPage)
  const firstRows = Array.isArray(first?.data) ? first.data : []
  const serverLastPage = Math.max(1, Number(first?.meta?.last_page) || 1)
  const lastPage = Math.min(serverLastPage, maxPages)
  const reportedTotal = Number(first?.meta?.total)
  const total = Number.isFinite(reportedTotal) ? reportedTotal : firstRows.length

  const rows = [...firstRows]
  onPage?.(firstRows, { page: 1, lastPage, total })

  for (let page = 2; page <= lastPage; page++) {
    const body = await fetchPage(page, perPage)
    const pageRows = Array.isArray(body?.data) ? body.data : []
    rows.push(...pageRows)
    onPage?.(pageRows, { page, lastPage, total })
    if (pageRows.length < perPage) break
  }

  return { rows, total, truncated: serverLastPage > maxPages }
}
